import { ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { createAsyncThunk } from '@reduxjs/toolkit';
import cloneDeep from 'lodash/cloneDeep';
import forEach from 'lodash/forEach';

import { checkInputs, checkMemory, checkLiquid, checkRegex, checkTopics } from './validateUtils';
import { definedMemoriesAndExActionNames, flattenSteps } from './utils';
import { getTypeSpecs } from './typeSpecsActions';
import { saveRevision } from './revisionActions';
import {
  changeCurrentThread,
  selectCurrentThread,
  selectCustomerId,
  selectModuleSpecsMap,
  selectTypeSpecs,
  setChildThreads,
  setErrors,
  setInitialThreadState,
  setIsDirty,
  setLastCreatedThreadId,
  setShowDisableThreadNotAllowedModal,
  setShowLoadingIndicator,
  setShowLoadingModal,
  transformThread,
} from './threadsUiSlice';
import sidekickThreadsResource from 'src/actions/sidekick/sidekickThreadsResource';
import SidekickThread from 'src/models/Sidekick/SidekickThreads';
import { toastError, toastSuccess } from 'src/actions/toast';
import { externalActionsDiff } from './externalActionHelper';

import { DomainError as StandardDomainError, Unauthorized } from '@ad-scripts/application/lib/error';
import { logout } from 'src/actions/auth/authSlice';
import { translateErrors } from 'src/adapters/validateType';
import sidekickFastThreadsResource from 'src/actions/sidekick/sidekickFastThreadsResource';
import { getThreadsParents } from './moduleHelper';
import { escapeString, unescapeHTML } from 'src/actions/sidekick/utils';

export const alterThreadByExpertMode = createAsyncThunk(
  'threadUi/alterThreadByExpertMode',
  async (thread: SidekickThread, { dispatch, getState }: any) => {
    dispatch(setShowLoadingIndicator(true));

    const currentThread = selectCurrentThread(getState());

    const changedThread = transformThread(thread, true);

    const alteredThread = { ...currentThread, nextData: changedThread.nextData };
    alteredThread.name = escapeString(changedThread.name);
    await dispatch(changeCurrentThread(alteredThread));
    await dispatch(saveThread({ revisionName: 'Expert mode alteration' }));
  }
);

export const saveThread = createAsyncThunk(
  'threadUi/saveThread',
  async (
    { revisionName }: { revisionName: string; doNotRefetchThreads?: boolean },
    { dispatch, getState, extra: { gateways } }: any
  ) => {
    dispatch(setShowLoadingIndicator(true));

    const currentThread = cloneDeep(selectCurrentThread(getState()));
    const customerId = selectCustomerId(getState());
    currentThread.name = escapeString(currentThread.name.trim());

    try {
      await validate(currentThread, customerId, getState, gateways, dispatch);
    } catch (e) {
      dispatch(toastError('There was an error while validating this item.'));
      dispatch(setShowLoadingIndicator(false));
      return;
    }
    await dispatch(
      sidekickThreadsResource.effects.update({ id: currentThread.id, customerId }, currentThread, {
        disableWindowBack: true,
        customToastMsg: `${unescapeHTML(currentThread.name)} has been updated.`,
      })
    );

    await dispatch(saveRevision({ thread: currentThread, reason: escapeString(revisionName) }));

    await dispatch(changeCurrentThread(currentThread));
    await dispatch(setInitialThreadState(currentThread));
    dispatch(setIsDirty(false));

    dispatch(setShowLoadingIndicator(false));
  }
);

export const updateThread = createAsyncThunk(
  'threadUi/updateThread',
  async (thread: SidekickThread, { dispatch, getState }: any) => {
    const customerId = selectCustomerId(getState());

    const { update } = sidekickThreadsResource.effects;
    const { fetchAll } = sidekickFastThreadsResource.effects;

    const copyThread = cloneDeep(thread);
    copyThread.name = escapeString(copyThread.name);
    await dispatch(update({ id: thread.id, customerId }, copyThread, { disableWindowBack: true }));

    await dispatch(
      fetchAll({ customerId }, {}, { body: { nameAsc: true, limit: 750 }, headers: { 'X-Method': 'GET' } })
    );
  }
);

export const enableThread = createAsyncThunk(
  'threadUi/enableThread',
  async (
    { threadId, enabled, ignorePivots }: { threadId: number; enabled: boolean; ignorePivots?: boolean },
    { dispatch, getState, extra: { gateways } }: any
  ) => {
    try {
      dispatch(setShowLoadingModal(true));
      const customerId = selectCustomerId(getState());

      const { fetchAll } = sidekickFastThreadsResource.effects;

      const thread = await gateways.sidekickThreads.fetch({ id: threadId, customerId });
      const rawThread = thread.data;

      if (rawThread?.enabled !== enabled) {
        if (!ignorePivots && rawThread.enabled) {
          const threads = await gateways.sidekickThreads.fetchAll({ customerId });
          const threadParentsMap = getThreadsParents(threads?.data || []);
          const pivotThreads = threadParentsMap[threadId];
          const activePivotThreads = pivotThreads.filter(thread => thread.enabled);
          const hasChildren = !!activePivotThreads.length;

          if (hasChildren) {
            dispatch(setChildThreads(activePivotThreads));
            dispatch(setShowDisableThreadNotAllowedModal(true));
            return;
          }
        }

        rawThread.enabled = enabled;
        await dispatch(
          sidekickThreadsResource.effects.update({ id: rawThread.id, customerId }, rawThread, {
            disableWindowBack: true,
            customToastMsg: `${unescapeHTML(rawThread.name)} has been ${enabled ? 'enabled' : 'disabled'}.`,
          })
        );

        await dispatch(saveRevision({ thread: rawThread, reason: 'Thread has been renamed.' }));
      }

      await dispatch(
        fetchAll({ customerId }, {}, { body: { nameAsc: true, limit: 750 }, headers: { 'X-Method': 'GET' } })
      );
    } catch (err) {
      handleError(err, `There was an error while ${enabled ? 'enabled' : 'disabled'}`, dispatch);
    } finally {
      dispatch(setShowLoadingModal(false));
    }
  }
);

export const renameThread = createAsyncThunk(
  'threadUi/renameThread',
  async (
    { threadId, newName }: { threadId: number; newName: string },
    { dispatch, getState, extra: { gateways } }: any
  ) => {
    try {
      const { fetchAll } = sidekickFastThreadsResource.effects;

      dispatch(setShowLoadingIndicator(true));
      const customerId = selectCustomerId(getState());

      const thread = await gateways.sidekickThreads.fetch({ id: threadId, customerId });
      const rawThread = thread.data;
      rawThread.name = escapeString(newName);

      await dispatch(
        sidekickThreadsResource.effects.update({ id: rawThread.id, customerId }, rawThread, {
          disableWindowBack: true,
          customToastMsg: `${unescapeHTML(rawThread.name)} has been renamed.`,
        })
      );

      await dispatch(saveRevision({ thread: rawThread, reason: 'Thread has been renamed.' }));

      await dispatch(
        fetchAll({ customerId }, {}, { body: { nameAsc: true, limit: 750 }, headers: { 'X-Method': 'GET' } })
      );
    } catch (err) {
      handleError(err, 'There was an error while renaming', dispatch);
    } finally {
      dispatch(setShowLoadingIndicator(false));
    }
  }
);

export const createThread = createAsyncThunk(
  'threadUi/createThread',
  async ({ threadName }: { threadName: string }, { dispatch }: any) => {
    dispatch(
      createOrCopyThread({
        thread: {
          name: threadName,
          enabled: false,
          custom: true,
          stepData: [],
          stagingStepData: [],
          next: true,
          nextData: [],
        },
        options: {
          errorMessage: 'Error creating thread.',
          successMessage: `${threadName} has been added.`,
        },
      })
    );
  }
);

export const duplicateThread = createAsyncThunk(
  'threadUi/duplicateThread',
  async (
    { threadId, threadName }: { threadId; threadName: string },
    { dispatch, getState, extra: { gateways } }: any
  ) => {
    try {
      const customerId = selectCustomerId(getState());

      const rawThread = await gateways.sidekickThreads.fetch({ id: threadId, customerId });
      const thread = rawThread.data;
      delete thread.id;

      dispatch(
        createOrCopyThread({
          thread,
          options: {
            errorMessage: 'Error creating thread.',
            successMessage: `${unescapeHTML(threadName)} has been added.`,
          },
        })
      );
    } catch (err) {
      handleError(err, 'There was an error while duplicating', dispatch);
    }
  }
);

export const createOrCopyThread = createAsyncThunk(
  'threadUi/createOrCopyThread',
  async (
    {
      thread,
      options,
    }: {
      thread: Partial<SidekickThread>;
      options: { successMessage: string | ReactNode; errorMessage: string | ReactNode };
    },
    { dispatch, getState, extra: { gateways } }: any
  ) => {
    try {
      const { fetchAll } = sidekickFastThreadsResource.effects;
      dispatch(sidekickThreadsResource.actions.addStarted({}));

      const customerId = selectCustomerId(getState());

      const copyThread = cloneDeep(thread) as Omit<SidekickThread, 'id'>;
      copyThread.name = escapeString(copyThread.name);
      const rawResult = await gateways.sidekickThreads.add({ customerId, ...copyThread }, copyThread);
      const result = rawResult.data as SidekickThread;
      await dispatch(saveRevision({ thread: result, reason: 'Thread created' }));

      await dispatch(
        fetchAll({ customerId }, {}, { body: { nameAsc: true, limit: 750 }, headers: { 'X-Method': 'GET' } })
      );

      dispatch(toastSuccess((options.successMessage as string) || 'Add successful!'));

      dispatch(setLastCreatedThreadId(result.id));
    } catch (err) {
      handleError(err, (options.errorMessage as string) || 'There was an error while adding', dispatch);
    }
  }
);

export const handleError = (err: any, errorMessage: string, dispatch: any) => {
  Sentry.captureException(err);
  if (err instanceof StandardDomainError) {
    dispatch(sidekickThreadsResource.actions.addError(translateErrors(err.errors)));
  } else if (err instanceof Unauthorized) {
    dispatch(logout());
  } else {
    dispatch(sidekickThreadsResource.actions.addRequestError(err));
  }

  dispatch(toastError(errorMessage));
};

export const validateThread = createAsyncThunk(
  'threadUi/validateThread',
  async (_, { dispatch, getState, extra: { gateways } }: any) => {
    const currentThread = selectCurrentThread(getState());
    const customerId = selectCustomerId(getState());

    await dispatch(getTypeSpecs());

    try {
      await validate(currentThread, customerId, getState, gateways, dispatch);
    } catch (err) {
      Sentry.captureException(err);
      dispatch(toastError('There was an error while validating this item.'));
    }
  }
);

const validate = async (
  thread: SidekickThread,
  customerId: string,
  getState,
  gateways,
  dispatch
): Promise<{ attr: string; code: string; detail: string }[] | never> => {
  dispatch(setErrors([]));

  const state = getState();

  const { value: customer } = (getState() as { sidekickCustomer }).sidekickCustomer;
  const { values: externalActions } = getState().sidekickExternalActions;
  const currentThread = selectCurrentThread(state);
  const specsMap = selectModuleSpecsMap(state);
  const types = cloneDeep(selectTypeSpecs(state));

  const steps = flattenSteps(thread.nextData, '', specsMap);

  const defer: Promise<{ attr: string; code: string; detail: string }[]>[] = [];
  forEach(steps, (step, i) => {
    const [mems, extActionMemNames] = definedMemoriesAndExActionNames(
      steps.slice(0, i + 1),
      currentThread.name,
      customer,
      specsMap,
      types
    );

    defer.push(checkMemory(step, extActionMemNames as string[], { types, mems }));
    defer.push(checkInputs(step));
    defer.push(checkLiquid(step, { customerId, memoryTypes: mems, types }, { gateways, getState, dispatch }));
    defer.push(checkRegex(step, { customerId }, { gateways, dispatch, getState }));
    defer.push(checkTopics(step, { getState }));
  });

  // include external action differences
  defer.push(externalActionsDiff(currentThread, externalActions));

  return await Promise.all(defer).then(errors => {
    const errs = errors.filter((err: unknown[]) => err && err.length > 0).flat();
    dispatch(setErrors(errs));
    return errs;
  });
};
